import { Node, Edge } from "reactflow";
import api from "core/api/api";
import statusManagement from "src/status-management";
import { buildComponentToFlowMapping } from "./cross-flow.utils";
import { EDGE_COLORS, EDGE_STYLES } from "./flow.constants";
import { EdgeType } from "../flow/flow.constants";

// ============================================================================
// EDGE STYLING UTILITIES
// ============================================================================

/**
 * Gets edge color based on app status
 */
export const getEdgeColor = (appStatus: string | null): string => {
    if (!appStatus) return EDGE_COLORS.DEFAULT;

    switch (appStatus) {
        case "DEPLOYED":
        case "RUNNING":
            return EDGE_COLORS.RUNNING;
        default:
            return EDGE_COLORS.DEFAULT;
    }
};

/**
 * Updates edges with animation and color based on app status
 */
export const updateEdgesWithStatus = (edges: Edge[], appStatus: string | null): Edge[] => {
    if (!appStatus || edges.length === 0) return edges;

    const isRunning = statusManagement.isApplicationRunning(appStatus);
    const edgeColor = getEdgeColor(appStatus);

    return edges.map(edge => ({
        ...edge,
        animated: isRunning,
        style: {
            ...edge.style,
            strokeWidth: EDGE_STYLES.DEFAULT_WIDTH,
            stroke: edgeColor
        }
    }));
};

/**
 * Gets default edge options based on app status
 */
export const getDefaultEdgeOptions = (appStatus: string | null) => ({
    type: "simplebezier" as const,
    animated: appStatus ? statusManagement.isApplicationRunning(appStatus) : false,
    style: {
        strokeWidth: EDGE_STYLES.DEFAULT_WIDTH,
        stroke: getEdgeColor(appStatus)
    }
});

/**
 * Recursively finds all ancestor nodes (parents) from a given node up to the root
 */
const findAllAncestors = (nodeId: string, edges: Edge[], visited: Set<string> = new Set()): Set<string> => {
    // Prevent infinite loops in case of cycles
    if (visited.has(nodeId)) {
        return visited;
    }
    visited.add(nodeId);

    // Find all edges where the current node is the target (incoming edges)
    const parentEdges = edges.filter(edge => edge.target === nodeId);

    // Recursively find ancestors of each parent
    parentEdges.forEach(edge => {
        findAllAncestors(edge.source, edges, visited);
    });

    return visited;
};

/**
 * Highlights edges connected to a specific node and all its ancestors (parent path from root to node)
 */
export const highlightConnectedEdges = (edges: Edge[], nodeId: string | null, appStatus: string | null): Edge[] => {
    if (!nodeId) {
        return edges.map(edge => ({
            ...edge,
            style: {
                ...edge.style,
                strokeWidth: EDGE_STYLES.DEFAULT_WIDTH,
                stroke: getEdgeColor(appStatus)
            },
            zIndex: undefined // Remove zIndex when not highlighting
        }));
    }

    // Find all ancestor nodes (parents from root to the hovered node)
    const ancestorNodes = findAllAncestors(nodeId, edges);

    const highlightColor = EDGE_COLORS.HIGHLIGHT;
    const defaultColor = getEdgeColor(appStatus);

    return edges.map(edge => {
        // Highlight edge if:
        // 1. Both source and target are in the ancestor path (including the hovered node)
        // 2. This ensures we highlight the complete flow from root to the hovered node
        const isInAncestorPath = ancestorNodes.has(edge.source) && ancestorNodes.has(edge.target);

        return {
            ...edge,
            style: {
                ...edge.style,
                stroke: isInAncestorPath ? highlightColor : defaultColor,
                strokeWidth: isInAncestorPath ? EDGE_STYLES.HIGHLIGHT_WIDTH : EDGE_STYLES.DEFAULT_WIDTH
            },
            zIndex: isInAncestorPath ? EDGE_STYLES.HIGHLIGHT_Z_INDEX : undefined
        };
    });
};

// ============================================================================
// EDGE CONNECTION UTILITIES
// ============================================================================

/**
 * Helper function to create a connection with edge lifting support
 */
export const createConnection = (
    sourceId: string,
    targetId: string,
    componentToFlowMap: Map<string, string>
): { source: string; target: string } | null => {
    if (!sourceId || !targetId) return null;

    // Apply edge lifting: if source/target is inside a flow, connect to the flow instead
    const actualSource = componentToFlowMap.get(sourceId) || sourceId;
    const actualTarget = componentToFlowMap.get(targetId) || targetId;

    return { source: actualSource, target: actualTarget };
};

/**
 * Helper function to add an edge with proper validation and cross-flow support
 */
export const addEdge = (
    edges: Edge[],
    sourceId: string,
    targetId: string,
    componentToFlowMap: Map<string, string>,
    visibleNodeIds: Set<string>,
    nodes?: Node[],
    isViewingSpecificFlow?: boolean
): void => {
    const connection = createConnection(sourceId, targetId, componentToFlowMap);
    if (connection) {
        // Check if both source and target nodes are visible
        // For cross-flow connections, the flow node should be visible instead of the component
        const sourceVisible = visibleNodeIds.has(connection.source);
        const targetVisible = visibleNodeIds.has(connection.target);

        if (sourceVisible && targetVisible && connection.source !== connection.target) {
            // Prevent Flow-to-Flow connections ONLY when viewing a specific flow
            if (isViewingSpecificFlow && nodes) {
                const sourceNode = nodes.find(node => node.id === connection.source);
                const targetNode = nodes.find(node => node.id === connection.target);

                if (sourceNode?.data?.componentType === "FLOW" && targetNode?.data?.componentType === "FLOW") {
                    return; // Block Flow-to-Flow connections in Flow view
                }
            }

            const edgeId = `${connection.source}-${connection.target}`;

            // Check if edge already exists
            if (!edges.find(edge => edge.id === edgeId)) {
                edges.push({
                    id: edgeId,
                    source: connection.source,
                    target: connection.target,
                    type: EdgeType
                });
            }
        }
    }
};

// ============================================================================
// COMPONENT CONNECTION PROCESSING
// ============================================================================

/**
 * Process component connections and create edges
 */
export const processComponentConnections = (
    component: any,
    componentId: string,
    edges: Edge[],
    componentToFlowMap: Map<string, string>,
    visibleNodeIds: Set<string>,
    nodes?: Node[],
    isViewingSpecificFlow?: boolean,
    appFQN?: string
): void => {
    const componentType = component.type;

    // If component not in flow map and not in current Flow, add to App level in flow map
    if (appFQN && !componentToFlowMap.has(componentId) && !visibleNodeIds.has(componentId)) {
        componentToFlowMap.set(componentId, appFQN);
    }

    // Handle SOURCE/EXTERNALSOURCE -> STREAM connections
    if ((componentType === "SOURCE" || componentType === "EXTERNALSOURCE") && component.outputStream) {
        addEdge(
            edges,
            componentId,
            component.outputStream,
            componentToFlowMap,
            visibleNodeIds,
            nodes,
            isViewingSpecificFlow
        );
    }

    // Handle STREAM -> TARGET connections
    if (componentType === "TARGET" && component.inputStream) {
        addEdge(
            edges,
            component.inputStream,
            componentId,
            componentToFlowMap,
            visibleNodeIds,
            nodes,
            isViewingSpecificFlow
        );
    }

    // Handle CQ connections
    if (componentType === "CQ") {
        // Handle input nodes -> CQ connections (via inputs array)
        if (component.inputs && Array.isArray(component.inputs)) {
            component.inputs.forEach((inputNodeId: string) => {
                addEdge(
                    edges,
                    inputNodeId,
                    componentId,
                    componentToFlowMap,
                    visibleNodeIds,
                    nodes,
                    isViewingSpecificFlow
                );
            });
        }

        // Handle CQ -> output node connection (via output property)
        if (component.output) {
            addEdge(
                edges,
                componentId,
                component.output,
                componentToFlowMap,
                visibleNodeIds,
                nodes,
                isViewingSpecificFlow
            );
        }
    }

    // Handle STREAM -> WINDOW connections
    if (componentType === "WINDOW" && component.stream) {
        addEdge(edges, component.stream, componentId, componentToFlowMap, visibleNodeIds, nodes, isViewingSpecificFlow);
    }

    // Handle ROUTER connections
    if (componentType === "ROUTER") {
        // STREAM -> ROUTER (input connection)
        if (component.inStream) {
            addEdge(
                edges,
                component.inStream,
                componentId,
                componentToFlowMap,
                visibleNodeIds,
                nodes,
                isViewingSpecificFlow
            );
        }

        // ROUTER -> STREAMs (output connections via forwardingRules)
        if (component.forwardingRules && Array.isArray(component.forwardingRules)) {
            component.forwardingRules.forEach((rule: any) => {
                if (rule.outStream) {
                    addEdge(
                        edges,
                        componentId,
                        rule.outStream,
                        componentToFlowMap,
                        visibleNodeIds,
                        nodes,
                        isViewingSpecificFlow
                    );
                }
            });
        }
    }

    // Handle OPENPROCESSOR connections
    if (componentType === "OPENPROCESSOR") {
        // node -> OPENPROCESSOR (input connection via 'from')
        // The server serializes `from` as an array of input FQNs (an OP can have multiple
        // inputs), but tolerate a single string too.
        if (component.from) {
            const inputs = Array.isArray(component.from) ? component.from : [component.from];
            inputs.forEach((inputId: string) => {
                addEdge(edges, inputId, componentId, componentToFlowMap, visibleNodeIds, nodes, isViewingSpecificFlow);
            });
        }

        // OPENPROCESSOR -> node (output connection via 'output')
        if (component.output) {
            addEdge(
                edges,
                componentId,
                component.output,
                componentToFlowMap,
                visibleNodeIds,
                nodes,
                isViewingSpecificFlow
            );
        }
    }

    // Handle SENTINEL connections
    if (componentType === "SENTINEL") {
        // STREAM -> SENTINEL (input connection via 'inputStream')
        if (component.inputStream) {
            addEdge(
                edges,
                component.inputStream,
                componentId,
                componentToFlowMap,
                visibleNodeIds,
                nodes,
                isViewingSpecificFlow
            );
        }

        // SENTINEL -> STREAM (output connection via 'outputStream')
        if (component.outputStream) {
            addEdge(
                edges,
                componentId,
                component.outputStream,
                componentToFlowMap,
                visibleNodeIds,
                nodes,
                isViewingSpecificFlow
            );
        }
    }
};

// ============================================================================
// MAIN EDGE CREATION FUNCTIONS
// ============================================================================

/**
 * Creates edges between nodes based on their stream connections.
 * This follows the same logic as the old flow designer in FlowDatasource.js
 *
 * Handles multiple types of connections:
 * 1. SOURCE/EXTERNALSOURCE -> STREAM (via outputStream property)
 * 2. STREAM -> TARGET (via inputStream property on target)
 * 3. input nodes -> CQ (via inputs array on CQ) and CQ -> output node (via output property on CQ)
 * 4. STREAM -> WINDOW (via stream property on WINDOW)
 * 5. STREAM -> ROUTER (via inStream property) and ROUTER -> STREAMs (via forwardingRules)
 * 6. node -> OPENPROCESSOR (via from property) and OPENPROCESSOR -> node (via output property)
 * 7. STREAM -> SENTINEL (via inputStream property) and SENTINEL -> STREAM (via outputStream property)
 */
export const createEdgesFromOutputStreams = (nodes: Node[], isViewingSpecificFlow?: boolean): Edge[] => {
    const edges: Edge[] = [];

    // Build component-to-flow mapping and create visible node IDs set
    const componentToFlowMap = buildComponentToFlowMapping(nodes);
    const visibleNodeIds = new Set<string>(nodes.map(node => node.id));

    // Process each visible node to find connections
    nodes.forEach(node => {
        const metaObject = node.data?.metaObject;
        if (!metaObject) return;

        // Create a component-like object from the node's metaObject for processing
        const component = {
            id: node.id,
            type: node.data?.componentType || metaObject.type,
            ...metaObject
        };

        processComponentConnections(
            component,
            node.id,
            edges,
            componentToFlowMap,
            visibleNodeIds,
            nodes,
            isViewingSpecificFlow
        );
    });

    return edges;
};

/**
 * Create edges from actual component metadata for flow-to-flow connections
 * This function fetches the actual component data and creates lifted edges
 */
export const createEdgesFromComponentData = async (
    appFQN: string,
    nodes: Node[],
    appComponents: any[],
    isViewingSpecificFlow?: boolean
): Promise<Edge[]> => {
    const edges: Edge[] = [];

    // Build component-to-flow mapping and create visible node IDs set
    const componentToFlowMap = buildComponentToFlowMapping(nodes);
    const visibleNodeIds = new Set<string>(nodes.map(node => node.id));

    // Pre-populate the map with app-level components (not in any flow, not currently visible)
    // so edges that reference them lift to the App placeholder regardless of iteration order.
    appComponents.forEach((component: any) => {
        if (!componentToFlowMap.has(component.id) && !visibleNodeIds.has(component.id)) {
            componentToFlowMap.set(component.id, appFQN);
        }
    });

    try {
        // Process each component to find connections using the shared helper function
        appComponents.forEach((component: any) => {
            processComponentConnections(
                component,
                component.id,
                edges,
                componentToFlowMap,
                visibleNodeIds,
                nodes,
                isViewingSpecificFlow,
                appFQN
            );
        });
    } catch (error) {
        console.error("Failed to fetch application components for edge lifting:", error);
    }

    return edges;
};
